import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useNavigate, useLocation } from "react-router-dom";
import { UserProvider, useUser } from "../contexts/UserContext";
import { ChakraProvider, extendTheme, Spinner, Center } from "@chakra-ui/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RequireUser } from "./RequireUser";
import { RequireAdmin } from "./RequireAdmin";
import { Home } from "./Home";
import { Registry } from "./Registry";
import { Admin } from "./Admin";
import { PatientDetail } from "./Admin/PatientDetail";
import { Accounts } from "./Admin/Accounts";
import { AccountDetail } from "./Admin/AccountDetail";
import App from "./App";
import { useTranslation } from "react-i18next";
import { SupportedLocale, ogLocales, isDefaultLocale } from "../i18n";
import { useUpdateUserLocale } from "../api/user/user";

// Initialize i18n
import "../i18n";

const queryClient = new QueryClient();

const styles = {
  global: {
    ".chakra-collapse": {
      overflow: "initial !important",
    },
  },
};

const chakraTheme = extendTheme({
  styles,
  colors: {
    blue: {
      50: "#E6EEFF",
      100: "#B8D0FE",
      200: "#8BB2FE",
      300: "#5D94FE",
      400: "#3076FD",
      500: "#0258FD",
      600: "#0246CA",
      700: "#013598",
      800: "#012365",
      900: "#001233",
    },
    gray: {
      50: "#F2F2F2",
      100: "#DBDBDB",
      200: "#C4C4C4",
      300: "#ADADAD",
      400: "#969696",
      500: "#808080",
      600: "#666666",
      700: "#4D4D4D",
      800: "#333333",
      900: "#1A1A1A",
    },
  },
  components: { Form: { baseStyle: { requiredIndicator: { color: "red.600" } } } },
});

// Loading fallback for i18n
const LoadingFallback = () => (
  <Center minHeight="100vh">
    <Spinner size="xl" />
  </Center>
);

// Module-level flag so it persists across LocaleLayout unmount/remount cycles
// (switching from /de to / creates a new component instance)
let initialRedirectDone = false;

// Layout component that sets the locale
const LocaleLayout: React.FC<{ locale: SupportedLocale }> = ({ locale }) => {
  const { i18n } = useTranslation("common");
  const { user, loggedIn, isLoading } = useUser();
  const updateLocaleMutation = useUpdateUserLocale();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract primitive value to avoid re-running effect when user object reference changes
  const userLocale = user?.locale;

  // Sync i18n language with URL locale
  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  // Sync locale preference to backend when user changes language via URL
  // Uses primitive userLocale to prevent effect re-runs on user object reference changes
  useEffect(() => {
    if (loggedIn && userLocale && userLocale !== locale && !updateLocaleMutation.isPending) {
      updateLocaleMutation.mutate(locale);
    }
    // Note: updateLocaleMutation excluded from deps - mutate is stable, and including
    // the mutation object causes unnecessary re-runs on state transitions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, loggedIn, userLocale]);

  // On initial load at root, redirect to user's saved locale preference
  useEffect(() => {
    if (initialRedirectDone || isLoading || !loggedIn || !user) return;
    if (location.pathname !== "/" || locale !== "en") return;
    if (user.locale === "en") return;

    initialRedirectDone = true;
    navigate(`/${user.locale}/`, { replace: true });
  }, [isLoading, loggedIn, user, locale, location.pathname, navigate]);

  // Update document metadata when locale changes
  useEffect(() => {
    const t = i18n.getFixedT(locale, "common");
    const baseUrl = "https://patients.science.xyz";
    const canonicalUrl = isDefaultLocale(locale) ? baseUrl : `${baseUrl}/${locale}`;

    // HTML lang attribute - important for accessibility/screen readers
    document.documentElement.lang = locale;

    // Document title
    document.title = t("meta.title");

    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("meta.description"));
    }

    // Canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", canonicalUrl);
    }

    // Open Graph tags
    document.querySelector('meta[property="og:title"]')?.setAttribute("content", t("meta.title"));
    document
      .querySelector('meta[property="og:description"]')
      ?.setAttribute("content", t("meta.description"));
    document.querySelector('meta[property="og:url"]')?.setAttribute("content", canonicalUrl);
    document
      .querySelector('meta[property="og:image:alt"]')
      ?.setAttribute("content", t("meta.ogImageAlt"));
    document
      .querySelector('meta[property="og:locale"]')
      ?.setAttribute("content", ogLocales[locale]);

    // Twitter tags
    document.querySelector('meta[name="twitter:title"]')?.setAttribute("content", t("meta.title"));
    document
      .querySelector('meta[name="twitter:description"]')
      ?.setAttribute("content", t("meta.description"));
    document.querySelector('meta[name="twitter:url"]')?.setAttribute("content", canonicalUrl);
    document
      .querySelector('meta[name="twitter:image:alt"]')
      ?.setAttribute("content", t("meta.ogImageAlt"));
  }, [locale, i18n]);

  return (
    <App>
      <Outlet />
    </App>
  );
};

const Main = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <UserProvider>
        <BrowserRouter>
          <ChakraProvider theme={chakraTheme}>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* German routes with /de prefix */}
                <Route path="/de" element={<LocaleLayout locale="de" />}>
                  <Route index element={<Home />} />
                  <Route element={<RequireUser />}>
                    <Route path="registry/*" element={<Registry />} />
                  </Route>
                  <Route element={<RequireAdmin />}>
                    <Route path="admin" element={<Admin />} />
                    <Route path="admin/patient/:id" element={<PatientDetail />} />
                    <Route path="admin/accounts" element={<Accounts />} />
                    <Route path="admin/accounts/:id" element={<AccountDetail />} />
                  </Route>
                </Route>

                {/* English routes at root (no prefix) */}
                <Route path="/" element={<LocaleLayout locale="en" />}>
                  <Route index element={<Home />} />
                  <Route element={<RequireUser />}>
                    <Route path="registry/*" element={<Registry />} />
                  </Route>
                  <Route element={<RequireAdmin />}>
                    <Route path="admin" element={<Admin />} />
                    <Route path="admin/patient/:id" element={<PatientDetail />} />
                    <Route path="admin/accounts" element={<Accounts />} />
                    <Route path="admin/accounts/:id" element={<AccountDetail />} />
                  </Route>
                </Route>
              </Routes>
            </Suspense>
          </ChakraProvider>
        </BrowserRouter>
      </UserProvider>
    </QueryClientProvider>
  );
};

export default Main;
